import {
  appointmentSlot,
  foldForSearch,
  inquiryPurposeLabel,
  resolveInspectionResult,
  townOf,
} from '@ghp/domain'
import {
  Badge,
  Card,
  DataTable,
  DataTableRowAction,
  EmptyState,
  Page,
  PageBody,
  PageHeader,
  SearchField,
  SegmentedControl,
  Tag,
  UrgentStack,
} from '@ghp/ui'
import { useQuery } from '@tanstack/react-query'
import { createRoute, useNavigate } from '@tanstack/react-router'
import { CalendarRange, CalendarX, ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'

import { useDocumentTitle } from '../../../app/document-title'
import { shellRoute } from '../../../app/routes/root'
import { useTeamData } from '../../../app/use-team-data'
import type { CachedInquiry, OutboxEntry } from '../../../offline/data-db'
import { latestSubmissions } from '../../../offline/outbox'
import { INQUIRIES_QUERY_KEY, OUTBOX_QUERY_KEY } from '../../../offline/query-keys'
import { OpeningSheet } from '../components/OpeningSheet'
import { RESULT_PRESENTATION } from '../labels'
import { tourStateOf, hasQueuedHandOver } from '../lib/list-filters'
import { useLockedInquiryIds } from '../lib/locked-drafts'
import s from './history.module.css'

/**
 * « Historique » — **les contrôles que cette tablette a transmis**, par période.
 *
 * ⚠️ Ce n'est pas la vue « Transmis » de « Mes contrôles » avec un filtre de dates, et les deux
 * ne peuvent pas partager leur source : cette vue-là répond à « où en est mon travail », donc un
 * contrôle que le bureau renvoie pour correction en **ressort** (`partitionScopes` le remet dans
 * « À faire »). L'historique, lui, est un **journal** : le contrôle a bel et bien été transmis à
 * une date, et l'effacer de l'historique parce qu'il est revenu retirerait la seule trace, sur
 * la tablette, de ce que l'équipe a rendu ce jour-là.
 *
 * La matière est donc la file d'envoi (D4), qui ne perd jamais d'entrée : le **dernier** envoi
 * accepté de chaque contrôle, sa date, et la conclusion telle qu'elle est **partie** — pas celle
 * que le brouillon porterait aujourd'hui.
 */
export const historyRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/history',
  component: History,
})

/**
 * Les trois périodes du prototype (`PERIODS`), en mois glissants, et la façon dont l'écran les
 * dit — « sur le dernier mois », « sur l'année ». Une seule table : le sélecteur, le résumé et
 * l'état vide nomment forcément la même période.
 */
const PERIODS = [
  { value: 'month', label: 'Mois', months: 1, span: 'le dernier mois' },
  { value: 'quarter', label: 'Trimestre', months: 3, span: 'le dernier trimestre' },
  { value: 'year', label: 'Année', months: 12, span: 'l’année' },
] as const

type Period = (typeof PERIODS)[number]['value']

/** Le prototype ouvre l'écran sur l'année (`historique`, `period || 'annee'`). */
const DEFAULT_PERIOD: Period = 'year'

/** Une ligne d'historique : l'envoi accepté, et la demande qu'il documente. */
interface HistoryRow {
  entry: OutboxEntry
  /** `undefined` si la demande n'est plus lisible dans le périmètre de l'équipe connectée. */
  inquiry: CachedInquiry | undefined
  /** Quand le serveur a accepté — non nul par construction, la ligne n'existe pas sans. */
  sentAt: string
}

function History() {
  const data = useTeamData()
  const navigate = useNavigate()
  const [period, setPeriod] = useState<Period>(DEFAULT_PERIOD)
  const [search, setSearch] = useState('')
  const [opening, setOpening] = useState<HistoryRow | null>(null)
  const locked = useLockedInquiryIds()

  useDocumentTitle('Historique')

  /**
   * L'instant de référence des périodes, figé à l'ouverture de l'écran.
   *
   * Relu à chaque rendu, il déplacerait la borne sous la frappe de l'agent : une ligne du 12 du
   * mois dernier entrerait et sortirait de la liste entre deux lettres tapées dans la recherche.
   */
  const [openedAt] = useState(() => new Date())

  const outbox = useQuery({
    queryKey: OUTBOX_QUERY_KEY,
    queryFn: () => data.outbox.toArray(),
  })
  const inquiries = useQuery({
    queryKey: INQUIRIES_QUERY_KEY,
    queryFn: () => data.inquiries.toArray(),
  })

  /**
   * Un envoi **accepté** par contrôle, le plus récent en tête.
   *
   * `latestSubmissions` replie la file une fois : elle porte tous les envois de la vie de la
   * tablette — rien ne l'allège jamais — et l'interroger par contrôle la rebalaierait autant de
   * fois qu'elle a de lignes.
   */
  const rows: HistoryRow[] = useMemo(() => {
    const byId = new Map((inquiries.data ?? []).map((inquiry) => [inquiry.id, inquiry]))

    return [...latestSubmissions(outbox.data ?? []).values()]
      .filter((entry) => entry.state === 'sent' && entry.sentAt !== null)
      .map((entry) => ({
        entry,
        inquiry: byId.get(entry.inquiryId),
        sentAt: entry.sentAt ?? '',
      }))
      .toSorted((left, right) => right.sentAt.localeCompare(left.sentAt))
  }, [outbox.data, inquiries.data])

  const span = PERIODS.find((option) => option.value === period) ?? PERIODS[2]
  const since = monthsBefore(openedAt, span.months).toISOString()
  const inPeriod = rows.filter((row) => row.sentAt >= since)
  const visible = inPeriod.filter((row) => matches(row, search))

  return (
    <Page>
      <PageHeader eyebrow="Contrôles transmis" title="Historique">
        <SegmentedControl
          label="Période"
          value={period}
          onValueChange={setPeriod}
          options={PERIODS.map(({ value, label }) => ({ value, label }))}
        />
        <SearchField
          className={s.search}
          label="Rechercher un contrôle transmis"
          placeholder="Rechercher…"
          value={search}
          onValueChange={setSearch}
        />
      </PageHeader>

      <PageBody>
        {inPeriod.length === 0 ? (
          <EmptyState
            icon={<CalendarX size={18} aria-hidden />}
            title="Aucun contrôle transmis"
          >{`Aucun contrôle transmis sur ${span.span}.`}</EmptyState>
        ) : (
          <>
            <p className={s.summary}>
              <CalendarRange size={16} aria-hidden />
              <span className={s.mono}>{inPeriod.length}</span>
              {` contrôle${inPeriod.length > 1 ? 's' : ''} transmis sur ${span.span}`}
            </p>

            <Card>
              <DataTable>
                <thead>
                  <tr>
                    <th scope="col" className={s.caseReference}>
                      Dossier
                    </th>
                    <th scope="col" className={s.reference}>
                      Réf. GHP
                    </th>
                    <th scope="col">Adresse</th>
                    {/* La colonne du prototype, revenue le 22/09/2026 : elle manquait parce que
                        la tournée ne descendait pas le type, et elle le descend depuis que le
                        modèle le porte. */}
                    <th scope="col" className={s.purpose}>
                      Type
                    </th>
                    <th scope="col" className={s.conclusion}>
                      Conclusion
                    </th>
                    <th scope="col" className={s.sentAt}>
                      Transmis
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => (
                    <HistoryLine key={row.entry.id} row={row} onOpen={setOpening} />
                  ))}
                </tbody>
              </DataTable>
              {visible.length === 0 && (
                <p className={s.noMatch} role="status">
                  Aucun contrôle transmis ne correspond à cette recherche.
                </p>
              )}
            </Card>
          </>
        )}

        {/*
         * La même pré-fiche que la tournée, parce que le prototype emploie le même `data-open`
         * dans les deux listes : l'adresse et le contact **avant** d'ouvrir une fiche de plus.
         * Sur un contrôle transmis, elle propose la consultation — c'est l'état qui le dit.
         */}
        <OpeningSheet
          inquiry={opening?.inquiry ?? null}
          state={
            opening?.inquiry === undefined
              ? undefined
              : tourStateOf({
                  inquiry: opening.inquiry,
                  sync: opening.entry.state,
                  sentAt: opening.sentAt,
                })
          }
          queued={
            opening !== null &&
            hasQueuedHandOver(opening.entry.state, locked.has(opening.entry.inquiryId))
          }
          onClose={() => {
            setOpening(null)
          }}
          onOpen={(inquiry) => {
            setOpening(null)
            void navigate({
              to: '/inspections/$inquiryId',
              params: { inquiryId: inquiry.id },
              search: { view: 'transmitted' },
            })
          }}
        />
      </PageBody>
    </Page>
  )
}

/** Ce qu'affiche une cellule dont la donnée manque — même signe que « Mes contrôles ». */
const MISSING = '—'

/**
 * ⚠️ **La cible est la rangée entière**, comme au prototype (`<tr data-open>`, `histTable`) :
 * `DataTableRowAction` l'étale depuis la référence, et la rangée d'un contrôle absent du cache
 * n'en porte aucune — rien à ouvrir, donc ni curseur ni survol.
 */
function HistoryLine({ row, onOpen }: { row: HistoryRow; onOpen: (row: HistoryRow) => void }) {
  const { inquiry, entry } = row
  // La conclusion **telle qu'elle est partie** : l'enveloppe est figée à la mise en file, et
  // c'est elle que le bureau a reçue. La relire dans le brouillon afficherait une conclusion que
  // personne n'a transmise si la fiche a été rouverte depuis.
  const result = RESULT_PRESENTATION[resolveInspectionResult(entry.submission.payload).value]

  return (
    <tr data-urgent={inquiry?.urgent === true ? '' : undefined}>
      <td className={s.mono}>
        <UrgentStack urgent={inquiry?.urgent}>{inquiry?.caseReference ?? MISSING}</UrgentStack>
      </td>
      <td className={s.mono}>
        {inquiry === undefined ? (
          MISSING
        ) : (
          <DataTableRowAction
            aria-label={`Ouvrir le contrôle ${inquiry.reference ?? 'sans référence'}`}
            onClick={() => {
              onOpen(row)
            }}
          >
            {inquiry.reference ?? 'Sans référence'}
            <ChevronRight size={16} aria-hidden className={s.chevron} />
          </DataTableRowAction>
        )}
      </td>
      <td>
        <span className={s.street}>{inquiry?.address.street ?? 'Adresse inconnue'}</span>
        {inquiry != null && townOf(inquiry.address) !== null && (
          <span className={s.city}>{townOf(inquiry.address)}</span>
        )}
      </td>
      <td>
        {/* La demande, pas l'envoi : le type ne voyage pas dans la charge utile, et il n'a pas à
            y voyager — c'est une donnée de la demande, que le bureau peut corriger après coup. Un
            cache antérieur à ce champ le relit sans valeur (D22) : le tiret, pas d'écran cassé. */}
        <Tag>{inquiryPurposeLabel(inquiry?.purpose ?? null) ?? MISSING}</Tag>
      </td>
      <td>
        <Badge tone={result.tone}>{result.label}</Badge>
      </td>
      {/* Le jour entier, **année comprise** : l'écran couvre jusqu'à douze mois, où « 4 août » ne
          dit plus de quelle année il parle. */}
      <td className={s.stamp}>{appointmentSlot(row.sentAt)?.day ?? MISSING}</td>
    </tr>
  )
}

/**
 * La borne basse d'une période, en mois glissants.
 *
 * ⚠️ `setMonth` déborde : le 31 mars moins un mois donne le 3 mars, et la période couvrirait
 * alors **plus** que le mois annoncé. `setDate(0)` ramène au dernier jour du mois visé.
 */
function monthsBefore(from: Date, months: number): Date {
  const bound = new Date(from)
  bound.setMonth(bound.getMonth() - months)
  if (bound.getDate() !== from.getDate()) bound.setDate(0)

  return bound
}

/**
 * La recherche du prototype, **sur ce que la ligne affiche** : références et adresse.
 *
 * Les accents sont retirés des deux côtés — une rue sur deux du secteur en porte un, et un agent
 * qui tape « sevre » au clavier de la tablette ne doit pas repartir bredouille.
 */
function matches(row: HistoryRow, search: string): boolean {
  const needle = foldForSearch(search.trim())
  if (needle === '') return true

  const haystack = [
    row.inquiry?.caseReference,
    row.inquiry?.reference,
    row.inquiry?.address.street,
    row.inquiry?.address.postalCode,
    row.inquiry?.address.city,
    // Le bourg que la ligne imprime (`townOf`) : sans lui, « montjean » ne trouvait rien sous une
    // ligne qui affiche « Montjean-sur-Loire » — la tournée, elle, le cherche déjà.
    row.inquiry?.address.delegatedName,
  ]

  return haystack.some((field) => field != null && foldForSearch(field).includes(needle))
}
